/**
 * MoJ comparison cluster — /moj/compare/{slug}.
 *
 * ACCURACY POLICY (ตรวจทาน 2026-08-03)
 * - นักแปลขึ้นทะเบียนกระทรวงยุติธรรมรับรอง "ความถูกต้องของคำแปล"
 * - ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (Notarial Services Attorney)
 *   ขึ้นทะเบียนกับสภาทนายความ รับรองลายมือชื่อ/สำเนา/คำสาบาน
 * - นิติกรณ์: กองสัญชาติและนิติกรณ์ กรมการกงสุล กระทรวงการต่างประเทศ
 *   200 บาท/ตราประทับ (ปกติ) · 400 บาท/ตราประทับ (ด่วน) — consular.mfa.go.th
 * - ไทยยังไม่ออก Apostille จนกว่าอนุสัญญาจะมีผลกับไทย 28 ก.พ. 2027
 * - ห้ามระบุราคาบริการของเรา · ห้ามให้คำมั่นเรื่องผลการพิจารณา · เวลาเป็นช่วงโดยประมาณ
 */

export interface MojCompareRow {
  aspect: string;
  a: string;
  b: string;
}

export interface MojCompare {
  slug: string;
  title: string;
  description: string;
  h1: string;
  aLabel: string;
  bLabel: string;
  answer: string;
  rows: MojCompareRow[];
  chooseA: string[];
  chooseB: string[];
  faqs: Array<{ q: string; a: string }>;
  synonyms: string[];
}

const TAIL =
  "ข้อมูล ณ ส.ค. 2026 — ระเบียบของหน่วยงานปลายทางเปลี่ยนแปลงได้ โปรดยืนยันรูปแบบเอกสารกับผู้รับเอกสารก่อนยื่นทุกครั้ง";

export const MOJ_COMPARE: MojCompare[] = [
  {
    slug: "moj-vs-notary",
    title: "แปลรับรองนักแปล MoJ กับ Notary Public ต่างกันอย่างไร",
    description:
      "เทียบทีละข้อระหว่างการรับรองความถูกต้องของคำแปลโดยนักแปลขึ้นทะเบียนกระทรวงยุติธรรม กับการรับรองลายมือชื่อและสำเนาโดยทนายความผู้ทำคำรับรอง ว่าแต่ละแบบใช้กับปลายทางใด และเมื่อไหร่ต้องใช้ทั้งคู่",
    h1: "แปลรับรอง MoJ vs Notary Public: รับรองคนละสิ่งกัน",
    aLabel: "นักแปลขึ้นทะเบียน MoJ",
    bLabel: "ทนายความผู้ทำคำรับรอง (Notary)",
    answer:
      "สองอย่างนี้ไม่ใช่ทางเลือกแทนกัน แต่รับรองคนละสิ่ง นักแปลขึ้นทะเบียนกระทรวงยุติธรรมรับรองว่าคำแปลตรงกับต้นฉบับ ส่วนทนายความผู้ทำคำรับรองลายมือชื่อและเอกสารรับรองว่าใครลงลายมือชื่อ หรือสำเนาตรงกับต้นฉบับ หลายเคสที่ยื่นต่างประเทศต้องใช้ทั้งสองอย่างต่อกัน แล้วจึงไปนิติกรณ์ที่กรมการกงสุล",
    rows: [
      { aspect: "รับรองอะไร", a: "ความถูกต้องของคำแปลเทียบต้นฉบับ", b: "ลายมือชื่อ สำเนาถูกต้อง คำสาบาน" },
      { aspect: "ผู้รับรอง", a: "นักแปลที่ขึ้นทะเบียนกับกระทรวงยุติธรรม", b: "ทนายความที่ได้รับอนุญาตจากสภาทนายความ" },
      { aspect: "ปลายทางที่มักกำหนด", a: "ศาลไทย หน่วยงานราชการไทย และเป็นฐานของงานยื่นต่างประเทศ", b: "สถานทูต มหาวิทยาลัย ธนาคาร และหน่วยงานต่างประเทศที่ต้องการยืนยันลายมือชื่อ" },
      { aspect: "ต้องมาด้วยตนเองไหม", a: "ไม่ต้อง ส่งไฟล์สแกนเริ่มงานได้", b: "ผู้ลงนามต้องแสดงตนต่อทนายความในขั้นรับรองลายมือชื่อ" },
      { aspect: "ใช้ต่อกันได้ไหม", a: "ใช้เป็นขั้นแรกของสายงานเอกสารต่างประเทศ", b: "ใช้ต่อจากคำแปลเมื่อปลายทางต้องการยืนยันลายมือชื่อผู้แปลหรือผู้ลงนาม" },
      { aspect: "ขั้นถัดไปที่พบบ่อย", a: "นิติกรณ์กรมการกงสุล 200 บาท/ตราประทับ (ด่วน 400 บาท)", b: "นิติกรณ์กรมการกงสุล แล้วจึงสถานทูตปลายทางถ้ากำหนด" },
    ],
    chooseA: [
      "ปลายทางขอ certified translation หรือคำแปลที่รับรองความถูกต้อง",
      "ยื่นศาลไทยหรือหน่วยงานราชการไทยที่ต้องการคำแปลภาษาไทย",
      "เป็นขั้นแรกก่อนนำเอกสารไปนิติกรณ์เพื่อใช้ต่างประเทศ",
    ],
    chooseB: [
      "ปลายทางขอ notarised signature หรือ certified true copy",
      "ต้องทำคำสาบานหรือ Affidavit ประกอบเอกสาร",
      "เอกสารต้องยืนยันว่าผู้ลงนามคือบุคคลนั้นจริง",
    ],
    faqs: [
      { q: "ต้องทำทั้งสองอย่างเสมอไหม", a: "ไม่เสมอไป ขึ้นกับเช็กลิสต์ของผู้รับเอกสาร งานที่ยื่นภายในประเทศจำนวนมากใช้คำแปลที่รับรองความถูกต้องก็เพียงพอ ส่วนงานที่ยื่นต่างประเทศมักกำหนดหลายชั้น วิธีที่แม่นที่สุดคือขอถ้อยคำตามเช็กลิสต์ของปลายทางมาอ่านก่อนเริ่มงาน " + TAIL },
      { q: "ทำผิดลำดับแล้วต้องเริ่มใหม่ไหม", a: "ในหลายกรณีต้องเริ่มขั้นรับรองใหม่ เพราะแต่ละชั้นรับรองอ้างอิงเอกสารชั้นก่อนหน้า เช่น ถ้านำต้นฉบับไปนิติกรณ์ก่อน แต่ปลายทางต้องการให้คำแปลผ่านนิติกรณ์ด้วย ก็ต้องกลับเข้าคิวใหม่อีกรอบ " + TAIL },
      { q: "ค่าธรรมเนียมส่วนไหนเป็นของราชการ", a: "ค่าธรรมเนียมนิติกรณ์ของกองสัญชาติและนิติกรณ์ กรมการกงสุล คือ 200 บาทต่อตราประทับแบบปกติ และ 400 บาทต่อตราประทับแบบด่วน ส่วนค่าธรรมเนียมของสถานทูตปลายทางแต่ละแห่งกำหนดเอง ค่าบริการงานแปลและงานเดินเอกสารสอบถามได้ทางโทรศัพท์ LINE หรืออีเมล " + TAIL },
      { q: "นักแปลกับทนายเป็นคนเดียวกันได้ไหม", a: "เป็นคนละบทบาทตามกฎหมาย แม้บุคคลหนึ่งอาจมีคุณสมบัติทั้งสองอย่าง แต่การรับรองแต่ละชั้นต้องระบุฐานะที่ใช้รับรองให้ชัด เพื่อให้ผู้ตรวจปลายทางเห็นว่าใครรับรองอะไร " + TAIL },
    ],
    synonyms: ["moj vs notary", "แปลกระทรวงยุติธรรม ต่างกับ notary", "certified translation vs notarisation thailand"],
  },
  {
    slug: "moj-vs-sworn-translator",
    title: "แปลรับรองในไทยกับ Sworn Translator ต่างประเทศ ใช้แบบไหน",
    description:
      "เทียบระบบรับรองคำแปลของไทยกับระบบนักแปลสาบานตนในยุโรป ว่าเอกสารที่จะยื่นในประเทศใดควรแปลที่ไหน และกรณีใดที่แปลจากไทยแล้วปลายทางไม่รับ",
    h1: "แปลรับรองในไทย vs Sworn Translator ในประเทศปลายทาง",
    aLabel: "แปลรับรองในไทย",
    bLabel: "Sworn Translator ปลายทาง",
    answer:
      "ตัวตัดสินคือระบบกฎหมายของประเทศผู้รับเอกสาร บางประเทศในยุโรปกำหนดให้คำแปลต้องทำโดยนักแปลที่สาบานตนต่อศาลในประเทศนั้น คำแปลที่ทำจากไทยจึงไม่ถูกยอมรับแม้จะรับรองครบชั้น ขณะที่อีกหลายประเทศยอมรับคำแปลที่ทำในไทยเมื่อผ่านการรับรองตามเส้นทางที่กำหนด จึงต้องถามผู้รับเอกสารก่อนเริ่มงานเสมอ",
    rows: [
      { aspect: "ผู้รับรอง", a: "นักแปลขึ้นทะเบียนกระทรวงยุติธรรมของไทย", b: "นักแปลที่สาบานตนต่อศาลหรือหน่วยงานของประเทศปลายทาง" },
      { aspect: "ประเทศที่มักกำหนด", a: "ไทย และประเทศที่ยอมรับคำแปลจากต้นทางเมื่อรับรองครบชั้น", b: "หลายประเทศในยุโรปภาคพื้นทวีป เช่น เยอรมนี ฝรั่งเศส สเปน อิตาลี" },
      { aspect: "ขั้นรับรองต่อ", a: "นิติกรณ์กรมการกงสุล แล้วจึงสถานทูตปลายทางถ้ากำหนด", b: "มักไม่ต้องรับรองซ้ำ เพราะคำแปลมีผลในประเทศนั้นอยู่แล้ว" },
      { aspect: "เวลาโดยรวม", a: "ควบคุมได้มากกว่า เพราะทุกขั้นอยู่ในไทย", b: "ขึ้นกับคิวของนักแปลและการส่งเอกสารข้ามประเทศ" },
      { aspect: "ความเสี่ยงถูกปฏิเสธ", a: "เกิดเมื่อปลายทางระบุว่าต้องเป็นนักแปลสาบานตนในประเทศตน", b: "ต่ำสำหรับปลายทางนั้น แต่ใช้กับประเทศอื่นอาจไม่ได้" },
      { aspect: "เอกสารที่ต้องส่ง", a: "ต้นฉบับหรือสำเนารับรอง พร้อมสำเนาหนังสือเดินทางเพื่อสะกดชื่อ", b: "มักต้องส่งเอกสารที่ผ่านการรับรองจากประเทศต้นทางไปให้ก่อน" },
    ],
    chooseA: [
      "ปลายทางยอมรับคำแปลจากประเทศต้นทางเมื่อผ่านการรับรองครบชั้น",
      "ยื่นในประเทศไทย หรือยื่นสถานทูตต่างประเทศประจำประเทศไทย",
      "ต้องการควบคุมเวลาและติดตามงานทุกขั้นในที่เดียว",
    ],
    chooseB: [
      "เช็กลิสต์ปลายทางระบุคำว่า sworn translator หรือ traducteur assermenté",
      "ยื่นต่อศาลหรือทะเบียนราษฎรของประเทศที่ใช้ระบบนักแปลสาบานตน",
      "ปลายทางเคยปฏิเสธคำแปลจากต่างประเทศมาก่อน",
    ],
    faqs: [
      { q: "จะรู้ได้อย่างไรว่าปลายทางต้องการแบบไหน", a: "อ่านจากเอกสารของหน่วยงานผู้รับโดยตรง เช่น หน้าเว็บไซต์ของสถานทูต ศาล หรือทะเบียนราษฎรของประเทศนั้น และเก็บหน้านั้นเป็นไฟล์ไว้ ถ้าถ้อยคำกำกวม ให้ส่งอีเมลถามและเก็บคำตอบไว้เป็นหลักฐานประกอบการยื่น " + TAIL },
      { q: "แปลในไทยแล้วให้นักแปลปลายทางรับรองซ้ำได้ไหม", a: "บางประเทศไม่อนุญาตให้รับรองงานที่ผู้อื่นแปล เพราะผู้สาบานตนต้องรับผิดชอบคำแปลเอง ในทางปฏิบัติจึงมักต้องแปลใหม่ทั้งฉบับ การถามก่อนจึงประหยัดกว่าการทำสองรอบ " + TAIL },
      { q: "เอกสารไทยต้องรับรองอะไรก่อนส่งไปต่างประเทศ", a: "โดยทั่วไปเอกสารราชการไทยต้องผ่านนิติกรณ์ที่กองสัญชาติและนิติกรณ์ กรมการกงสุล ก่อน แล้วจึงประทับตราสถานทูตปลายทางถ้าสถานทูตนั้นกำหนด ค่าธรรมเนียมนิติกรณ์คือ 200 บาทต่อตราประทับแบบปกติ และ 400 บาทต่อตราประทับแบบด่วน " + TAIL },
      { q: "ไทยใช้ Apostille แทนได้แล้วหรือยัง", a: "ยังไม่ได้ ประเทศไทยเข้าเป็นภาคีอนุสัญญา Apostille แล้ว แต่จะมีผลใช้บังคับกับไทยวันที่ 28 กุมภาพันธ์ 2027 ก่อนหน้านั้นยังต้องใช้เส้นทางนิติกรณ์และสถานทูตตามเดิม " + TAIL },
    ],
    synonyms: ["sworn translator vs moj", "แปลสาบานตน ต่างประเทศ", "certified vs sworn translation thailand"],
  },
  {
    slug: "แปลก่อน-หรือ-รับรองก่อน",
    title: "แปลก่อนหรือรับรองต้นฉบับก่อน ลำดับไหนถูกต้อง",
    description:
      "เทียบสองลำดับงานที่คนสับสนบ่อยที่สุด คือแปลก่อนแล้วค่อยรับรอง กับรับรองต้นฉบับก่อนแล้วค่อยแปล ว่ากรณีใดใช้ลำดับใด และการสลับลำดับทำให้เสียรอบอย่างไร",
    h1: "แปลก่อน vs รับรองต้นฉบับก่อน",
    aLabel: "แปลก่อน แล้วรับรอง",
    bLabel: "รับรองต้นฉบับก่อน แล้วแปล",
    answer:
      "หลักคือชั้นรับรองต้องเดินตามลำดับที่ปลายทางกำหนด ถ้าปลายทางต้องการให้รับรองทั้งต้นฉบับและคำแปล ต้องเริ่มจากต้นฉบับที่หน่วยงานผู้ออกรับรองก่อน แล้วจึงแปลจากฉบับนั้น เพราะคำแปลต้องสะท้อนตราประทับที่ปรากฏบนเอกสารด้วย การแปลจากสำเนาที่ยังไม่รับรองแล้วค่อยไปรับรองภายหลัง เป็นสาเหตุการทำงานซ้ำที่พบบ่อยที่สุด",
    rows: [
      { aspect: "เหมาะกับกรณี", a: "ปลายทางต้องการเฉพาะคำแปลที่รับรองความถูกต้อง", b: "ปลายทางต้องการทั้งต้นฉบับและคำแปลที่ผ่านการรับรอง" },
      { aspect: "ความเสี่ยงหลัก", a: "ถ้าปลายทางขอตราประทับบนต้นฉบับด้วย ต้องแปลใหม่ให้ครอบคลุมตรานั้น", b: "ใช้เวลารอเอกสารรับรองก่อนเริ่มแปล" },
      { aspect: "ผลต่อจำนวนตราประทับ", a: "น้อยกว่า เพราะรับรองเฉพาะคำแปล", b: "มากกว่า เพราะนับตราประทับทั้งต้นฉบับและคำแปล" },
      { aspect: "เวลารวมโดยประมาณ", a: "สั้นกว่าเมื่อไม่ต้องรอเอกสารต้นทาง", b: "ยาวกว่า แต่ลดโอกาสต้องทำซ้ำ" },
      { aspect: "เอกสารที่มักใช้ลำดับนี้", a: "สัญญาเอกชน เอกสารภายในบริษัท", b: "เอกสารทะเบียนราษฎร คำพิพากษา หนังสือรับรองบริษัท" },
      { aspect: "จุดตรวจก่อนเริ่ม", a: "ยืนยันว่าปลายทางไม่ได้ขอรับรองต้นฉบับ", b: "ยืนยันว่าหน่วยงานผู้ออกเอกสารออกสำเนารับรองให้ได้" },
    ],
    chooseA: [
      "เอกสารเป็นสัญญาหรือเอกสารเอกชนที่ไม่มีหน่วยงานผู้ออกให้รับรอง",
      "ปลายทางระบุชัดว่าต้องการคำแปลที่รับรองความถูกต้องเท่านั้น",
      "ต้องใช้คำแปลเร่งด่วนเพื่อประกอบการพิจารณาเบื้องต้น",
    ],
    chooseB: [
      "เอกสารเป็นทะเบียนราษฎรหรือเอกสารศาลที่มีหน่วยงานผู้ออก",
      "ปลายทางกำหนดให้ทั้งต้นฉบับและคำแปลผ่านนิติกรณ์",
      "เคยถูกตีกลับเพราะต้นฉบับไม่มีตรารับรอง",
    ],
    faqs: [
      { q: "ทำผิดลำดับแล้วแก้ยังไง", a: "ส่วนใหญ่ต้องกลับไปขอเอกสารต้นฉบับที่รับรองแล้ว จากนั้นทบทวนคำแปลให้ครอบคลุมตราประทับและข้อความที่เพิ่มเข้ามา แล้วจึงเข้าคิวรับรองอีกครั้ง ต้นทุนที่แท้จริงคือเวลา ไม่ใช่เอกสาร จึงควรตรวจลำดับให้จบก่อนเริ่ม " + TAIL },
      { q: "รับรองคำแปลอย่างเดียวพอไหม", a: "พอสำหรับหลายงานในประเทศ แต่ไม่พอสำหรับปลายทางที่ตรวจย้อนไปถึงต้นฉบับ ให้ดูถ้อยคำในเช็กลิสต์ว่าเขียนว่า certified copy หรือ legalised original ไว้หรือไม่ ถ้ามี แปลว่าต้องรับรองต้นฉบับด้วย " + TAIL },
      { q: "เอกสารเก่ามากขอสำเนารับรองไม่ได้ ทำอย่างไร", a: "แจ้งข้อเท็จจริงต่อผู้รับเอกสารพร้อมหลักฐานว่าหน่วยงานผู้ออกไม่สามารถออกสำเนารับรองได้ บางปลายทางยอมรับหนังสือชี้แจงประกอบ แต่การตัดสินใจอยู่ที่ผู้รับเอกสาร ไม่ควรคาดเดาแทน " + TAIL },
      { q: "แปลจากไฟล์สแกนได้ไหมถ้าต้นฉบับยังอยู่ต่างจังหวัด", a: "เริ่มงานแปลจากไฟล์สแกนได้เพื่อประหยัดเวลา แต่ต้องยืนยันว่าไฟล์นั้นตรงกับฉบับที่จะใช้ยื่นจริง หากภายหลังได้ต้นฉบับที่มีตราประทับเพิ่ม ต้องปรับคำแปลให้ตรงก่อนเข้าขั้นรับรอง " + TAIL },
    ],
    synonyms: ["แปลก่อนหรือรับรองก่อน", "ลำดับรับรองเอกสาร", "translate before or after legalisation"],
  },
  {
    slug: "ยื่นเอง-vs-มอบให้ดำเนินการ",
    title: "เดินเรื่องเองกับให้ผู้ให้บริการดำเนินการแทน คุ้มกว่ากันตรงไหน",
    description:
      "เทียบตามเวลา ความเสี่ยงตีกลับ และค่าใช้จ่ายจริง ระหว่างการเดินเอกสารเองที่กรมการกงสุลและสถานทูต กับการมอบให้ผู้ให้บริการดำเนินการแทน",
    h1: "เดินเรื่องเอง vs ให้ดำเนินการแทน",
    aLabel: "เดินเรื่องเอง",
    bLabel: "ให้ดำเนินการแทน",
    answer:
      "ถ้าเอกสารมีไม่กี่ฉบับ ปลายทางเดียว และผู้ยื่นอยู่ในกรุงเทพหรือปริมณฑล การเดินเรื่องเองมักคุ้ม เพราะค่าธรรมเนียมราชการเท่ากันทุกช่องทาง แต่ถ้าเอกสารหลายฉบับ หลายปลายทาง หรือผู้ยื่นอยู่ต่างจังหวัดหรือต่างประเทศ การมอบให้ดำเนินการแทนมักคุ้มกว่าเมื่อคิดรวมค่าเดินทางและความเสี่ยงที่ต้องกลับมาเข้าคิวใหม่",
    rows: [
      { aspect: "ค่าธรรมเนียมราชการ", a: "จ่ายตามอัตราจริง 200 บาท/ตราประทับ ปกติ หรือ 400 บาท/ตราประทับ ด่วน", b: "อัตราเดียวกัน เพราะเป็นค่าธรรมเนียมของหน่วยงาน" },
      { aspect: "เวลาที่ผู้ยื่นต้องใช้", a: "ต้องจัดวันไปยื่นและวันไปรับ อย่างน้อยสองรอบต่อชั้นรับรอง", b: "ใช้เวลาเฉพาะขั้นเตรียมเอกสารและตรวจคำแปล" },
      { aspect: "ความเสี่ยงเอกสารไม่ครบ", a: "สูงกว่าเมื่อไม่คุ้นแบบฟอร์มและข้อกำหนดของแต่ละปลายทาง", b: "ลดลงเพราะมีการตรวจเช็กลิสต์ก่อนยื่น แต่ผลการพิจารณาเป็นดุลพินิจของหน่วยงานปลายทาง" },
      { aspect: "กรณีอยู่ต่างประเทศ", a: "ทำได้ยาก ต้องอาศัยผู้แทนในไทยอยู่ดี", b: "เหมาะกว่า โดยจัดทำหนังสือมอบอำนาจตามแบบที่ปลายทางยอมรับ" },
      { aspect: "การติดตามสถานะ", a: "ติดตามเองตามช่องทางของหน่วยงาน", b: "มีผู้ติดตามและแจ้งความคืบหน้าให้" },
      { aspect: "ค่าบริการ", a: "ไม่มี", b: "สอบถามค่าบริการทางโทรศัพท์ LINE หรืออีเมล ตามจำนวนฉบับและชั้นรับรอง" },
    ],
    chooseA: [
      "เอกสารไม่กี่ฉบับและปลายทางเดียว",
      "ผู้ยื่นอยู่ใกล้หน่วยงานและจัดเวลาไปเองได้",
      "เคยยื่นเอกสารชุดเดียวกันมาก่อนและรู้ขั้นตอนดี",
    ],
    chooseB: [
      "เอกสารหลายฉบับ หลายภาษา หรือหลายปลายทางพร้อมกัน",
      "ผู้ยื่นอยู่ต่างจังหวัดหรือต่างประเทศ",
      "มีกำหนดวันยื่นตายตัวและรับความเสี่ยงเข้าคิวใหม่ไม่ได้",
    ],
    faqs: [
      { q: "ให้ดำเนินการแทนแล้วเร็วกว่าจริงไหม", a: "ขั้นตอนที่เป็นคิวของหน่วยงานราชการย่นไม่ได้ไม่ว่าใครไปยื่น สิ่งที่ลดได้จริงคือรอบที่ต้องกลับไปแก้เอกสาร เพราะมีการตรวจความครบถ้วนก่อนยื่น และการเลือกช่องทางบริการด่วนของหน่วยงานเมื่อเหมาะสม " + TAIL },
      { q: "ต้องมอบอำนาจอย่างไร", a: "ใช้หนังสือมอบอำนาจตามแบบที่หน่วยงานผู้รับยอมรับ พร้อมสำเนาบัตรประชาชนหรือหนังสือเดินทางของผู้มอบอำนาจที่ลงนามรับรองสำเนา บางขั้นตอนที่ต้องยืนยันตัวบุคคลยังต้องดำเนินการด้วยตนเอง จึงควรตรวจก่อนว่าขั้นใดมอบอำนาจได้ " + TAIL },
      { q: "ค่าใช้จ่ายรวมประเมินอย่างไร", a: "แยกเป็นสองก้อนเสมอ คือค่าธรรมเนียมราชการที่จ่ายตามจำนวนตราประทับจริง กับค่าบริการงานแปลและงานเดินเอกสาร ก้อนแรกตรวจได้จากอัตราของหน่วยงาน ส่วนก้อนหลังสอบถามได้ทางโทรศัพท์ LINE หรืออีเมลตามขอบเขตงานจริง " + TAIL },
      { q: "มีการรับประกันว่าเอกสารผ่านไหม", a: "ไม่มีผู้ให้บริการใดรับประกันผลของหน่วยงานราชการหรือสถานทูตได้ สิ่งที่ทำได้คือเตรียมเอกสารให้ตรงเช็กลิสต์ ตรวจความครบถ้วนก่อนยื่น และแก้ไขตามข้อทักท้วงอย่างรวดเร็ว หากมีผู้รับประกันผลให้ ควรพิจารณาด้วยความระมัดระวัง " + TAIL },
    ],
    synonyms: ["ยื่นเอง หรือ จ้างเดินเรื่อง", "diy vs agency legalisation thailand", "เดินเอกสารเอง คุ้มไหม"],
  },
];

export const MOJ_COMPARE_BY_SLUG: Record<string, MojCompare> = Object.fromEntries(
  MOJ_COMPARE.map((c) => [c.slug, c]),
);
